import { useState, useEffect, useMemo } from 'react';
import { Plus, Pencil, Trash2, Hotel, Building, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { getHotelsPaginated, createHotel, updateHotel, deleteHotel, getZones } from '../lib/api';

const emptyHotel = { name: '', zone_id: '', price: null, active: true };
const PAGE_SIZE = 50;

export default function AdminHotels() {
  const [hotels, setHotels] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingHotel, setEditingHotel] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState(emptyHotel);
  const [filterZone, setFilterZone] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalHotels, setTotalHotels] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const zoneMap = useMemo(() => {
    const map = {};
    zones.forEach(z => { map[z.id] = z; });
    return map;
  }, [zones]);

  const destinationZones = useMemo(() => zones.filter(z => z.zone_type === 'destination'), [zones]);

  const filteredHotels = useMemo(() => {
    return hotels.filter(h => {
      const matchesZone = filterZone === 'all' || h.zone_id === filterZone;
      const matchesSearch = !searchTerm || h.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesZone && matchesSearch;
    });
  }, [hotels, filterZone, searchTerm]);

  useEffect(() => { setPage(1); }, [filterZone, searchTerm]);

  const fetchData = async () => {
    try {
      const zoneParam = filterZone !== 'all' ? filterZone : null;
      const [hotelsRes, zonesRes] = await Promise.all([
        getHotelsPaginated(page, PAGE_SIZE, zoneParam),
        zones.length ? Promise.resolve({ data: zones }) : getZones()
      ]);
      const paginatedData = hotelsRes.data;
      setHotels(paginatedData.data);
      setTotalHotels(paginatedData.total);
      setTotalPages(paginatedData.pages);
      if (zonesRes.data !== zones) setZones(zonesRes.data);
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [page, filterZone]);

  const openCreate = () => { setFormData(emptyHotel); setEditingHotel('new'); };
  const openEdit = (hotel) => { setFormData(hotel); setEditingHotel(hotel.id); };

  const handleSave = async () => {
    try {
      const saveData = { ...formData, price: formData.price || null };
      if (editingHotel === 'new') {
        await createHotel(saveData);
        toast.success('Hotel creado');
      } else {
        await updateHotel(editingHotel, saveData);
        toast.success('Hotel actualizado');
      }
      setEditingHotel(null);
      fetchData();
    } catch (error) {
      toast.error('Error guardando hotel');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteHotel(deleteId);
      toast.success('Hotel eliminado');
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error('Error eliminando hotel');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Cargando hoteles...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-hotels">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white lg:hidden">Hoteles</h1>
          <span className="text-sm text-slate-500 bg-white/5 px-3 py-1 rounded-full">
            {filteredHotels.length} de {totalHotels} hoteles
          </span>
        </div>
        <Button 
          onClick={openCreate}
          className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold"
          data-testid="add-hotel-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar Hotel
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Buscar hotel..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-white/5 border-white/10 text-white pl-10"
            data-testid="hotel-search"
          />
        </div>
        <Select value={filterZone} onValueChange={setFilterZone}>
          <SelectTrigger className="w-full sm:w-[250px] bg-white/5 border-white/10 text-white" data-testid="hotel-zone-filter">
            <SelectValue placeholder="Filtrar por zona" />
          </SelectTrigger>
          <SelectContent className="bg-[#1E293B] border-white/10 max-h-60">
            <SelectItem value="all" className="text-white hover:bg-white/10">Todas las Zonas</SelectItem>
            {destinationZones.map((zone) => (
              <SelectItem key={zone.id} value={zone.id} className="text-white hover:bg-white/10">
                {zone.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Hotels Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left text-sm font-medium text-slate-400 px-4 py-3">Hotel</th>
                <th className="text-left text-sm font-medium text-slate-400 px-4 py-3">Zona</th>
                <th className="text-center text-sm font-medium text-slate-400 px-4 py-3">Estado</th>
                <th className="text-right text-sm font-medium text-slate-400 px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredHotels.map((hotel) => (
                <tr key={hotel.id} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${!hotel.active && 'opacity-50'}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                        <Building className="w-4 h-4 text-orange-400" />
                      </div>
                      <span className="text-white text-sm font-medium">{hotel.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-400">{zoneMap[hotel.zone_id]?.name || 'Sin zona'}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      hotel.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {hotel.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white h-8 w-8 p-0" onClick={() => openEdit(hotel)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-8 w-8 p-0" onClick={() => setDeleteId(hotel.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredHotels.length === 0 && (
          <div className="p-12 text-center">
            <Hotel className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No se encontraron hoteles</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <Button size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            Anterior
          </Button>
          <span className="text-sm text-slate-400">
            Página {page} de {totalPages}
          </span>
          <Button size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            Siguiente
          </Button>
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingHotel} onOpenChange={() => setEditingHotel(null)}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{editingHotel === 'new' ? 'Agregar Hotel' : 'Editar Hotel'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Nombre del Hotel</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Hard Rock Hotel Punta Cana"
                data-testid="hotel-name-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Zona</label>
              <Select value={formData.zone_id} onValueChange={(value) => setFormData({ ...formData, zone_id: value })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="hotel-zone-select">
                  <SelectValue placeholder="Seleccionar zona" />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 max-h-60">
                  {destinationZones.map((zone) => (
                    <SelectItem key={zone.id} value={zone.id} className="text-white hover:bg-white/10">
                      {zone.name} (${zone.price})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Precio personalizado (vacío = precio de zona)</label>
              <Input
                type="number"
                value={formData.price || ''}
                onChange={(e) => setFormData({ ...formData, price: e.target.value ? parseFloat(e.target.value) : null })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Opcional"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-400">Activo</label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingHotel(null)} className="border-white/10 text-white hover:bg-white/5">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-hotel-btn">
              Guardar Hotel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminar Hotel?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
